
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight, Crown, Zap } from "lucide-react";
import { useSubscription } from "@/hooks/useSubscription";
import { UsageMeter } from "@/components/subscription/UsageMeter";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from "react-i18next";

export function SubscriptionStatus() {
  const { subscriptionData, isLoading, formatResetDate } = useSubscription();
  const { t } = useTranslation();

  if (isLoading) {
    return <SubscriptionStatusSkeleton />;
  }

  if (!subscriptionData) {
    return null;
  }

  const resetDateFormatted = formatResetDate(subscriptionData.resetDate);

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>
              Dit abonnement
            </CardTitle>
            <CardDescription>
              Forbrug og abonnementsinformation
            </CardDescription>
          </div>
          <Badge 
            variant={subscriptionData.isPremium ? "default" : "outline"}
            className={subscriptionData.isPremium ? "bg-primary" : ""}
          >
            {subscriptionData.isPremium && <Crown className="h-3.5 w-3.5 mr-1.5" />}
            {subscriptionData.plan}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <UsageMeter
            title="Oversættelsesforbrug"
            used={subscriptionData.translationUsage.used}
            limit={subscriptionData.translationUsage.limit}
            percentage={subscriptionData.translationUsage.percentage}
            resetDate={resetDateFormatted}
          />
          <UsageMeter
            title="Analyseforbrug"
            used={subscriptionData.analysisUsage.used}
            limit={subscriptionData.analysisUsage.limit}
            percentage={subscriptionData.analysisUsage.percentage}
            resetDate={resetDateFormatted}
          />
        </div>

        {!subscriptionData.isPremium && (
          <div className="mt-6">
            <Link to="/pricing">
              <Button variant="default" className="w-full">
                <Zap className="h-4 w-4 mr-2" />
                Opgrader til Premium
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SubscriptionStatusSkeleton() {
  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <Skeleton className="h-5 w-40 mb-2" />
            <Skeleton className="h-4 w-60" />
          </div>
          <Skeleton className="h-6 w-20" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Skeleton className="h-5 w-32 mb-1" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-2 w-full" />
            <Skeleton className="h-3 w-24 mt-1" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-5 w-32 mb-1" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-2 w-full" />
            <Skeleton className="h-3 w-24 mt-1" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
